/**
 * Cards Layout
 * 
 * Displays items as cards with image, title, and subtitle.
 * Based on reference: app/src/layouts/cards/
 */

import { useMemo, useCallback } from 'react';
import { Loader2, Image as ImageIcon, Check, Circle, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import type { LayoutProps, CardsLayoutOptions, LayoutItem } from '../types';

// Special value for "none" selection
const NONE_VALUE = '__none__';

interface CardProps {
    item: LayoutItem;
    itemKey: string;
    isSelected: boolean;
    selectMode: boolean;
    onSelect: () => void;
    onClick: () => void;
    size: number;
    icon?: string;
    imageSource?: string;
    title?: string;
    subtitle?: string;
    imageFit?: 'crop' | 'contain';
    primaryKeyField: string;
}

function Card({
    item,
    itemKey: _itemKey,
    isSelected,
    selectMode,
    onSelect,
    onClick,
    size,
    icon: _icon,
    imageSource,
    title,
    subtitle,
    imageFit = 'crop',
    primaryKeyField,
}: CardProps) {
    const cardSize = size * 40; // Base size multiplier

    // Get image URL from item
    const imageUrl = useMemo(() => {
        if (!imageSource) return null;
        const imageField = item[imageSource];
        if (!imageField) return null;

        // If it's a file object with id
        if (typeof imageField === 'object' && imageField !== null && 'id' in imageField) {
            return `/assets/${(imageField as { id: string }).id}?key=system-medium-cover`;
        }
        // If it's a direct URL or id string
        if (typeof imageField === 'string') {
            if (imageField.startsWith('http') || imageField.startsWith('/')) {
                return imageField;
            }
            return `/assets/${imageField}?key=system-medium-cover`;
        }
        return null;
    }, [item, imageSource]);

    // Render title from template or field
    const renderedTitle = useMemo(() => {
        if (!title) return item[primaryKeyField] as string;
        // Simple template rendering - replace {{ field }} with value
        return title.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, field) => {
            return String(item[field] ?? '');
        });
    }, [title, item, primaryKeyField]);

    // Render subtitle from template or field
    const renderedSubtitle = useMemo(() => {
        if (!subtitle) return null;
        return subtitle.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, field) => {
            return String(item[field] ?? '');
        });
    }, [subtitle, item]);

    const handleClick = useCallback(() => {
        if (selectMode) {
            onSelect();
        } else {
            onClick();
        }
    }, [selectMode, onSelect, onClick]);

    return (
        <div
            className={cn(
                "group relative flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-all hover:shadow-md cursor-pointer",
                isSelected && "ring-2 ring-primary"
            )}
            style={{ width: cardSize, minWidth: cardSize }}
            onClick={handleClick}
        >
            {/* Selection indicator */}
            <button
                type="button"
                className={cn(
                    "absolute top-2 left-2 z-10 flex h-6 w-6 items-center justify-center rounded-full transition-all",
                    isSelected
                        ? "bg-primary text-primary-foreground"
                        : "bg-background/80 text-muted-foreground opacity-0 group-hover:opacity-100"
                )}
                onClick={(e) => {
                    e.stopPropagation();
                    onSelect();
                }}
            >
                {isSelected ? (
                    <Check className="h-4 w-4" />
                ) : (
                    <Circle className="h-4 w-4" />
                )}
            </button>

            {/* Image or icon placeholder */}
            <div
                className={cn(
                    "relative flex items-center justify-center bg-muted",
                    imageFit === 'crop' ? 'aspect-square' : 'aspect-video'
                )}
            >
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={renderedTitle}
                        className={cn(
                            "h-full w-full",
                            imageFit === 'crop' ? 'object-cover' : 'object-contain'
                        )}
                        onError={(e) => {
                            // Hide broken image
                            (e.target as HTMLImageElement).style.display = 'none';
                        }}
                    />
                ) : (
                    <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                )}
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col gap-1 p-3">
                <h3 className="font-medium text-sm line-clamp-2 text-foreground">
                    {renderedTitle}
                </h3>
                {renderedSubtitle && (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                        {renderedSubtitle}
                    </p>
                )}
            </div>
        </div>
    );
}

export function CardsLayout({
    collection: _collection,
    primaryKeyField = 'id',
    items,
    loading = false,
    error,
    page: _page,
    limit: _limit,
    totalCount: _totalCount,
    itemCount: _itemCount,
    totalPages: _totalPages = 1,
    selection,
    onSelectionChange,
    showSelect = 'multiple',
    sort = [],
    onSortChange,
    layoutOptions,
    onLayoutOptionsChange,
    layoutQuery,
    onLayoutQueryChange,
    fields,
    onItemClick,
    readonly: _readonly = false,
}: LayoutProps<CardsLayoutOptions>) {

    const size = layoutOptions.size ?? 4;
    const selectMode = selection.length > 0;

    // Get sortable fields
    const sortableFields = useMemo(() => {
        return fields.filter(f => !f.meta?.hidden);
    }, [fields]);

    // Parse current sort value from sort array
    const currentSort = sort[0] || '';
    const sortField = currentSort.startsWith('-') ? currentSort.slice(1) : currentSort;
    const sortDirection = currentSort.startsWith('-') ? 'desc' : 'asc';

    // Handle sort field change
    const handleSortFieldChange = useCallback((field: string) => {
        if (!onSortChange) return;
        if (field === NONE_VALUE) {
            onSortChange([]);
            onLayoutQueryChange({ ...layoutQuery, sort: [] });
        } else {
            const newSort = sortDirection === 'desc' ? `-${field}` : field;
            onSortChange([newSort]);
            onLayoutQueryChange({ ...layoutQuery, sort: [newSort] });
        }
    }, [sortDirection, onSortChange, layoutQuery, onLayoutQueryChange]);

    // Handle sort direction change
    const handleSortDirectionChange = useCallback((direction: 'asc' | 'desc') => {
        if (!sortField || !onSortChange) return;
        const newSort = direction === 'desc' ? `-${sortField}` : sortField;
        onSortChange([newSort]);
        onLayoutQueryChange({ ...layoutQuery, sort: [newSort] });
    }, [sortField, onSortChange, layoutQuery, onLayoutQueryChange]);

    // Toggle single item selection
    const handleToggleSelection = useCallback((itemKey: string | number) => {
        if (selection.includes(itemKey)) {
            onSelectionChange(selection.filter(k => k !== itemKey));
        } else {
            onSelectionChange([...selection, itemKey]);
        }
    }, [selection, onSelectionChange]);

    // Handle card click
    const handleCardClick = useCallback((item: LayoutItem) => {
        if (onItemClick) {
            onItemClick(item);
        }
    }, [onItemClick]);

    // Handle size change
    const handleSizeChange = useCallback((newSize: number) => {
        onLayoutOptionsChange({ ...layoutOptions, size: newSize });
    }, [layoutOptions, onLayoutOptionsChange]);

    if (loading && items.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-destructive">Error loading items: {error.message}</p>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground">No items found</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header with sort and size controls */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
                <div className="flex items-center gap-4">
                    {showSelect === 'multiple' && selection.length > 0 && (
                        <span className="text-sm text-muted-foreground">
                            {selection.length} selected
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    {/* Sort Field */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Sort</span>
                        <Select
                            value={sortField || NONE_VALUE}
                            onValueChange={handleSortFieldChange}
                        >
                            <SelectTrigger className="h-8 w-32">
                                <SelectValue placeholder="Sort by" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NONE_VALUE}>None</SelectItem>
                                {sortableFields.map(f => (
                                    <SelectItem key={f.field} value={f.field}>
                                        {f.meta?.note || f.field}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {sortField && (
                            <div className="flex border rounded-md">
                                <Button
                                    variant={sortDirection === 'asc' ? 'secondary' : 'ghost'}
                                    size="icon"
                                    className="h-8 w-8 rounded-r-none"
                                    onClick={() => handleSortDirectionChange('asc')}
                                >
                                    <ArrowUp className="h-3 w-3" />
                                </Button>
                                <Button
                                    variant={sortDirection === 'desc' ? 'secondary' : 'ghost'}
                                    size="icon"
                                    className="h-8 w-8 rounded-l-none border-l"
                                    onClick={() => handleSortDirectionChange('desc')}
                                >
                                    <ArrowDown className="h-3 w-3" />
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Size Slider */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Size</span>
                        <Slider
                            value={[size]}
                            onValueChange={(values) => values[0] !== undefined && handleSizeChange(values[0])}
                            min={2}
                            max={8}
                            step={1}
                            className="w-20"
                        />
                    </div>
                </div>
            </div>

            {/* Cards Grid */}
            <div className="flex flex-wrap gap-6 p-4">
                {items.map((item) => {
                    const itemKey = item[primaryKeyField] as string | number;
                    const isSelected = selection.includes(itemKey);

                    return (
                        <Card
                            key={itemKey}
                            item={item}
                            itemKey={String(itemKey)}
                            isSelected={isSelected}
                            selectMode={selectMode}
                            onSelect={() => handleToggleSelection(itemKey)}
                            onClick={() => handleCardClick(item)}
                            size={size}
                            icon={layoutOptions.icon}
                            imageSource={layoutOptions.imageSource}
                            title={layoutOptions.title}
                            subtitle={layoutOptions.subtitle}
                            imageFit={layoutOptions.imageFit}
                            primaryKeyField={primaryKeyField}
                        />
                    );
                })}
            </div>
        </div>
    );
}
